// utils/presetHelpers.ts
// Preset system utilities - cleaned up version with only actively used functions

import { SCALE_QUALITY_ABBREVIATIONS } from './constants';
import type { CagedVisualizationSettings, Progression, ProgressionMetadata, GuitarArrangement, Note, Scale } from '../presets/presetSchema';
import { DEFAULT_CAGED_VISUALIZATIONS } from '../presets/presetSchema';
import { parseChord, type ParsedChord } from './chordParser';
import { generateGuitarId, generateProgressionId } from './idUtils';

/**
 * Generate a default preset name from guitars array
 * @param guitars - Array of guitar objects in schema format
 * @returns Generated preset name
 */
export function generatePresetName(guitars: GuitarArrangement[]): string {
  if (!guitars || guitars.length === 0) {
    return 'Empty Preset';
  }

  const parts = guitars.map(guitar => {
    const rootNote = guitar.root.note;
    const quality = guitar.root.scale;
    const suffix = SCALE_QUALITY_ABBREVIATIONS[quality] || '';
    return rootNote + suffix;
  });

  return parts.join(' ');
}

// ================================================================================
// CHORD SYMBOL PARSING
// ================================================================================

/**
 * Parse a chord symbol into root note and quality tags
 * Uses the comprehensive parseChord function and converts to legacy format
 */
export function parseChordSymbol(chordSymbol: string): { root: Note; quality: string } {
  const result = parseChord(chordSymbol);
  
  if (!result.isValid || !result.chord) {
    return { root: 'C', quality: 'MAJOR' };
  }
  
  // Convert from Scale enum to our legacy quality strings
  const qualityMap: Record<Scale, string> = {
    'MAJOR_TRIAD': 'MAJOR',
    'MINOR_TRIAD': 'MINOR',
    'DOMINANT_7TH': 'DOMINANT',
    'MAJOR_7TH': 'MAJOR7',
    'MINOR_7TH': 'MINOR7',
    'DIMINISHED_TRIAD': 'DIMINISHED',
    'DIMINISHED_7TH': 'DIMINISHED',
    'AUGMENTED_TRIAD': 'AUGMENTED',
    'HALF_DIMINISHED_7TH': 'MINOR7',
    'MINOR_MAJOR_7TH': 'MINOR7',
    'AUGMENTED_7TH': 'DOMINANT',
    'AUGMENTED_MAJOR_7TH': 'MAJOR7',
    // Fallback mappings for other scale types
    'MAJOR': 'MAJOR',
    'MINOR': 'MINOR',
    'HARMONIC_MINOR': 'MINOR',
    'MELODIC_MINOR': 'MINOR',
    'DORIAN': 'MINOR',
    'PHRYGIAN': 'MINOR',
    'LYDIAN': 'MAJOR',
    'MIXOLYDIAN': 'MAJOR',
    'LOCRIAN': 'MINOR',
    'PENTATONIC_MAJOR': 'MAJOR',
    'PENTATONIC_MINOR': 'MINOR',
    'BLUES': 'MINOR',
    'CHROMATIC': 'MAJOR',
    'WHOLE_TONE': 'MAJOR',
    'OCTATONIC_HALF_WHOLE': 'MAJOR',
    'OCTATONIC_WHOLE_HALF': 'MAJOR'
  };
  
  return {
    root: result.chord.root,
    quality: qualityMap[result.chord.quality] || 'MAJOR'
  };
}

/**
 * Convert chord-quality label into a Scale enum value
 */
export function getScaleFromChord(chordQuality: string): Scale {
  const map: Record<string, Scale> = {
    'MAJOR':'MAJOR_TRIAD',
    'MINOR':'MINOR_TRIAD',
    'DOMINANT':'DOMINANT_7TH',
    'MAJOR7':'MAJOR_7TH',
    'MINOR7':'MINOR_7TH',
    'DIMINISHED':'DIMINISHED_TRIAD',
    'AUGMENTED':'AUGMENTED_TRIAD'
  };
  return map[chordQuality] || 'MAJOR_TRIAD';
}

// ================================================================================
// PARSING & PROGRESSION BUILDER
// ================================================================================
interface ChordParseOptions {
  startingId?: number;
  cagedVisualizations?: CagedVisualizationSettings;
}

/**
 * Convert chord symbols to GuitarArrangement array
 */
export function parseChordSymbolsToGuitars(
  chords: string[],
  options: ChordParseOptions = {}
): GuitarArrangement[] {
  if (!Array.isArray(chords)) return [];
  return chords.map((symbol, idx) => {
    const parsed = parseChordSymbol(symbol);
    const scale = getScaleFromChord(parsed.quality);
    const id = options.startingId != null ? `${options.startingId + idx}` : generateGuitarId();
    return {
      id,
      root: { note: parsed.root, scale: scale, showScaleDegrees: false },
      overlay: undefined,
      caged: {
        activeShapes: {},
        quality: parsed.quality.includes('MINOR') ? 'MINOR' : 'MAJOR',
        visualizations: options.cagedVisualizations || DEFAULT_CAGED_VISUALIZATIONS
      }
    };
  });
}

/**
 * Convert ParsedChord objects to GuitarArrangement array
 */
export function parsedChordsToGuitars(
  chords: ParsedChord[],
  options: ChordParseOptions = {}
): GuitarArrangement[] {
  if (!Array.isArray(chords)) return [];
  return chords.map((chord, idx) => {
    const id = options.startingId != null ? `${options.startingId + idx}` : generateGuitarId();
    
    // Map the Scale enum to CAGED quality - check for minor chord types
    const cagedQuality = (chord.quality.includes('MINOR') || chord.quality.includes('DIMINISHED')) ? 'MINOR' : 'MAJOR';
    
    // Set a default active CAGED shape based on the chord root
    const getDefaultActiveShape = (root: string): string => {
      // Map chord roots to their optimal CAGED shapes for better fingering
      const shapeMap: Record<string, string> = {
        'C': 'C', 'D': 'D', 'E': 'E', 'F': 'E', 'G': 'G', 'A': 'A', 'B': 'A',
        'C#': 'C', 'Db': 'C', 'D#': 'D', 'Eb': 'D', 'F#': 'E', 'Gb': 'E',
        'G#': 'G', 'Ab': 'G', 'A#': 'A', 'Bb': 'A'
      };
      return shapeMap[root] || 'C'; // Default to C shape if not found
    };
    
    const defaultShape = getDefaultActiveShape(chord.root);
    const activeShapes = { [defaultShape]: true };
    
    return {
      id,
      root: { 
        note: chord.root, 
        scale: cagedQuality === 'MINOR' ? 'MINOR' : 'MAJOR', // Use basic scales instead of chord triads
        showScaleDegrees: false 
      },
      overlay: undefined,
      caged: {
        activeShapes,
        quality: cagedQuality,
        visualizations: options.cagedVisualizations || DEFAULT_CAGED_VISUALIZATIONS
      }
    };
  });
}

/**
 * Extract chord symbols from GuitarArrangement array
 */
export function extractChordSymbols(guitars: GuitarArrangement[]): string[] {
  if (!Array.isArray(guitars)) return [];
  return guitars.map(g => {
    const root = g.root.note;
    const quality = g.caged?.quality.toLowerCase() || 'major';
    return quality === 'major' ? root : `${root}${quality}`;
  });
}

/**
 * Build a full Progression object for saving
 */
export function createProgression(
  guitars: GuitarArrangement[],
  name: string,
  metadata: Partial<ProgressionMetadata> = {},
  id?: string
): Progression {
  return {
    id: id || generateProgressionId(),
    name,
    source: 'user',
    arrangements: guitars,
    metadata: { created: Date.now(), modified: Date.now(), guitarCount: guitars.length, version: '1.0', ...metadata }
  };
}
